/**
 * The outcome of a change to the open set, in one place, so that the question to the close
 * guards and the transition after it agree.
 */

import { getSummonedDependents } from '@/app/panelSummons';
import { URL_PROP_KEYS } from '@/app/panelUrl';
import { resolveAutoChild, resolveOpen } from '@/app/resolveOpen';
import type {
    DefiniteRequest, OpenPanel, OpenPlan, OpenPlanInput, PanelAdmits, PanelConfig, PanelId,
    PanelLocalProps, PanelMode, PanelOpenRequest, PanelPropsRequest, PanelRegistry, PropWrite,
    SharedPanelProps,
} from '@/types/panels';

import { onStage } from './shellPanels';

interface AutoOpenInput {
    panels: OpenPanel[];
    mode: PanelMode;
    localPropsByPanel: PanelLocalProps;
    seeds: Iterable<PanelId>;
    admits: PanelAdmits;
    registry: PanelRegistry;
}

/** The outcome of a request, or null when there is nothing to commit. */
export function planOpen(input: OpenPlanInput & { request: DefiniteRequest }): OpenPlan;
export function planOpen(input: OpenPlanInput): OpenPlan | null;
export function planOpen(input: OpenPlanInput): OpenPlan | null {
    const { request } = input;
    if (request.kind === 'open') {
        return planPanelOpen(input, request);
    }
    if (request.kind === 'props') {
        return planPropWrite(input, request);
    }
    return planAutoChild(input);
}

// `options.mode` forces the mode of the open. `options.also` opens more panels in the same commit,
// after the candidate, so the candidate wins a conflict and keeps the focus.
function planPanelOpen(input: OpenPlanInput, request: PanelOpenRequest): OpenPlan | null {
    const { openPanels, mode, admits, registry } = input;
    const { id, props, options, isOffer = false } = request;
    if (isOffer && !canStageShowOffer(input, id)) {
        return null;
    }
    const targetMode = resolveTargetMode(registry[id]?.config, mode, options?.mode);
    const modePanels = filterPanelsByMode(openPanels, mode, targetMode);
    const resolvedPanels = resolveCandidateWithAlso(modePanels, request, targetMode, registry);
    const expandedPanels = addAutoPanels(
        input, request, resolvedPanels, targetMode, admits(options?.focus ?? id));
    const write = { id, props: props ?? {} };
    const remainingPanels = removeLeavingDependents(openPanels, expandedPanels, write);
    const mergedProps = remainingPanels.find((panel) => panel.id === id)?.props ?? {};
    const urlProps = getWrittenUrlProps(mergedProps, write);
    const nextPanels = remainingPanels.map((panel) => overlayUrlProps(panel, urlProps));
    return {
        nextPanels,
        departingIds: getDepartingIds(openPanels, nextPanels, write, id),
        focusId: chooseFocusId(nextPanels, request, input),
        urlProps,
        localProps: splitProps(mergedProps).localProps,
        shouldReplace: getSummonedDependents(openPanels, new Set(), write).size > 0,
    };
}

function canStageShowOffer(
    { openPanels, focusedId, admits, registry }: OpenPlanInput, id: PanelId,
): boolean {
    const candidate = registry[id];
    return candidate !== undefined &&
        admits(focusedId)(openPanels, { id, props: {}, config: candidate.config });
}

function filterPanelsByMode(
    openPanels: OpenPanel[], currentMode: PanelMode, targetMode: PanelMode,
): OpenPanel[] {
    return targetMode === currentMode
        ? openPanels
        : openPanels.filter((panel) => panel.config.modes.includes(targetMode));
}

function resolveCandidateWithAlso(
    panels: OpenPanel[], { id, props, options }: PanelOpenRequest, mode: PanelMode,
    registry: PanelRegistry,
): OpenPanel[] {
    let resolvedPanels = resolveOpen({
        openPanels: panels,
        candidateId: id,
        candidateProps: props,
        mode,
        registry,
    });
    for (const alsoId of options?.also ?? []) {
        const panelsWithAlso = resolveOpen({
            openPanels: resolvedPanels,
            candidateId: alsoId,
            mode,
            registry,
        });
        if (panelsWithAlso.some((panel) => panel.id === id)) {
            resolvedPanels = panelsWithAlso;
        }
    }
    return resolvedPanels;
}

function addAutoPanels(
    { openPanels, localPropsByPanel, registry }: OpenPlanInput, request: PanelOpenRequest,
    resolvedPanels: OpenPanel[], mode: PanelMode, admits: PanelAdmits,
): OpenPanel[] {
    const withChild = resolveAutoChild({
        panels: resolvedPanels,
        candidateId: request.id,
        mode,
        registry,
        admits,
    });
    return appendAutoOpenIds({
        panels: withChild, mode, localPropsByPanel,
        seeds: getCommitPanelIds(openPanels, withChild, request), admits, registry,
    });
}

function getCommitPanelIds(
    openPanels: OpenPanel[], nextPanels: OpenPanel[], { id, options }: PanelOpenRequest,
): Set<PanelId> {
    const priorIds = getIds(openPanels);
    const seeds = new Set<PanelId>([id, ...(options?.also ?? [])]);
    for (const panel of nextPanels) {
        if (!priorIds.has(panel.id)) {
            seeds.add(panel.id);
        }
    }
    return seeds;
}

function removeLeavingDependents(
    openPanels: OpenPanel[], expandedPanels: OpenPanel[], write: PropWrite,
): OpenPanel[] {
    const stayingIds = getIds(expandedPanels);
    const evictedIds = new Set(openPanels
        .filter((panel) => !stayingIds.has(panel.id))
        .map((panel) => panel.id));
    const leavingIds = getSummonedDependents(openPanels, evictedIds, write);
    return expandedPanels.filter((panel) => !leavingIds.has(panel.id));
}

function getWrittenUrlProps(
    mergedProps: Record<string, unknown>, write: PropWrite,
): SharedPanelProps {
    return splitProps(Object.fromEntries(Object.entries(mergedProps)
        .filter(([key]) => Object.hasOwn(write.props, key)))).urlProps;
}

function chooseFocusId(
    nextPanels: OpenPanel[], { id, options }: PanelOpenRequest,
    { isSmViewport, focusedId }: OpenPlanInput,
): PanelId | null {
    const anchorId = [options?.focus, id].find((panelId) => (
        nextPanels.some((panel) => panel.id === panelId && onStage(panel, isSmViewport))
    ));
    return anchorId ?? focusedId;
}

function planPropWrite(input: OpenPlanInput, request: PanelPropsRequest): OpenPlan {
    const { openPanels, focusedId } = input;
    const props = toClearingProps(request.props);
    const write = { id: request.id, props };
    const leavingIds = getSummonedDependents(openPanels, new Set(), write);
    const nextPanels = openPanels
        .filter((panel) => !leavingIds.has(panel.id))
        .map((panel) => (
            panel.id === write.id ? { ...panel, props: { ...panel.props, ...props } } : panel));
    return {
        nextPanels,
        departingIds: getDepartingIds(openPanels, nextPanels, write, null),
        focusId: focusedId,
        ...splitProps(props),
        shouldReplace: leavingIds.size > 0,
    };
}

function toClearingProps(props: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(props).map(([key, value]) => [key, value ?? null]));
}

// Only the rightmost panel on the stage can get an auto child.
function planAutoChild(input: OpenPlanInput): OpenPlan | null {
    const { openPanels, mode, isSmViewport, focusedId, admits, registry } = input;
    const rightmost = openPanels.filter((panel) => onStage(panel, isSmViewport)).at(-1);
    if (!rightmost) {
        return null;
    }
    const nextPanels = resolveAutoChild({
        panels: openPanels,
        candidateId: rightmost.id,
        mode,
        registry,
        admits: admits(focusedId),
    });
    if (nextPanels.length === openPanels.length) {
        return null;
    }
    return {
        nextPanels, departingIds: [], focusId: focusedId, urlProps: {}, localProps: {},
        shouldReplace: false,
    };
}

/**
 * A forced mode that the candidate supports, else the only mode of the candidate (a
 * settings-only panel goes to settings), else the current mode.
 */
function resolveTargetMode(
    candidateConfig: Pick<PanelConfig, 'modes'> | undefined,
    currentMode: PanelMode,
    forcedMode?: PanelMode,
): PanelMode {
    const modes = candidateConfig?.modes ?? [];
    if (forcedMode !== undefined && modes.includes(forcedMode)) {
        return forcedMode;
    }
    return modes.length === 1 ? modes[0] ?? currentMode : currentMode;
}

function getIds(panels: OpenPanel[]): Set<PanelId> {
    return new Set(panels.map((panel) => panel.id));
}

// Appends the `autoOpenIds` of `seeds` and of each appended panel. The mode gate keeps the form
// out of the view, and `admits` keeps out a panel that the stage cannot show.
function appendAutoOpenIds({
    panels, mode, localPropsByPanel, seeds, admits, registry,
}: AutoOpenInput): OpenPanel[] {
    const outOpenPanels = [...panels];
    const seen = getIds(outOpenPanels);
    const expandable = new Set(seeds);
    for (const panel of outOpenPanels) {
        if (!expandable.has(panel.id)) {
            continue;
        }
        for (const id of panel.config.autoOpenIds) {
            if (seen.has(id)) {
                continue;
            }
            const autoOpenPanel = registry[id];
            if (!autoOpenPanel || !autoOpenPanel.config.modes.includes(mode)) {
                continue;
            }
            const entry = { id, props: localPropsByPanel[id] ?? {}, config: autoOpenPanel.config };
            if (!admits(outOpenPanels, entry)) {
                continue;
            }
            outOpenPanels.push(entry);
            seen.add(id);
            expandable.add(id);
        }
    }
    return outOpenPanels;
}

// The panels that close, and the panels whose subject the write changes. A shared prop moves
// each panel that declares it as a subject; an open moves its panel for each new value.
function getDepartingIds(
    openPanels: OpenPanel[], nextPanels: OpenPanel[], write: PropWrite, openedId: PanelId | null,
): PanelId[] {
    const nextIds = getIds(nextPanels);
    return openPanels
        .filter((panel) => {
            if (!nextIds.has(panel.id)) {
                return true;
            }
            if (panel.id === openedId) {
                return Object.entries(write.props).some(
                    ([key, value]) => value !== undefined && value !== panel.props[key]);
            }
            return doesChangeAny(panel, write.props, panel.config.subjectProps);
        })
        .map((panel) => panel.id);
}

/** Whether writing `props` changes any of `keys` on `panel`; null clears a prop. */
function doesChangeAny(
    panel: OpenPanel, props: Record<string, unknown>, keys: readonly string[],
): boolean {
    return keys.some((key) => {
        const nextValue = props[key];
        return nextValue !== undefined &&
            (nextValue ?? undefined) !== (panel.props[key] ?? undefined);
    });
}

/** `panel` with the URL props that it declares; null or undefined clears a prop. */
function overlayUrlProps(panel: OpenPanel, urlProps: SharedPanelProps): OpenPanel {
    const keys = panel.config.urlSyncedProps.filter((key) => Object.hasOwn(urlProps, key));
    if (keys.length === 0) {
        return panel;
    }
    const props = { ...panel.props };
    for (const key of keys) {
        const value = urlProps[key];
        if (value === null || value === undefined) {
            delete props[key];
        } else {
            props[key] = value;
        }
    }
    return { ...panel, props };
}

// The URL gets only URL_PROP_KEYS, because a shared link must not carry the choices of a viewer.
function splitProps(
    props: Record<string, unknown>,
): { urlProps: SharedPanelProps; localProps: Record<string, unknown> } {
    const urlProps: SharedPanelProps = {};
    const localProps: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(props)) {
        if (URL_PROP_KEYS.includes(key)) {
            urlProps[key] = value;
        } else {
            localProps[key] = value;
        }
    }
    return { urlProps, localProps };
}
